/**
 * NMS LP Scroll Video Controls
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles video controls for responsive scroll video section with separate mobile/desktop videos
 *
 * Features:
 * - Separate mobile and desktop videos that switch based on viewport
 * - Controls only the currently visible video
 * - Handles viewport resize events
 * - Play/Pause and Mute/Unmute functionality
 */

class NMSLPScrollVideoControls extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];
  #resizeTimeout = null;

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.sectionId = element.id || `nms-lp-scroll-video-${Date.now()}`; // REQUIRED: Unique identifier

    // REQUIRED: Store in global registry for external access
    window.neemans = window.neemans || {};
    window.neemans.lpScrollVideoControls = window.neemans.lpScrollVideoControls || {};
    window.neemans.lpScrollVideoControls[this.sectionId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupRefs();

    // Check if we have required elements
    if (!this.#validateElements()) {
      return;
    }

    this.#initializeState();
    this.#bindEvents();
    this.#initializeButtonStates();

    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupRefs() {
    const container = this.element.querySelector(`[data-video-container="${this.sectionId}"]`);

    if (!container) {
      this.handleError(new Error('Video container not found'), 'Missing video container');
      return;
    }

    // Store references
    this.refs.container = container;
    this.refs.mobileVideo = container.querySelector('.nms-lp-scroll-video__video--mobile');
    this.refs.desktopVideo = container.querySelector('.nms-lp-scroll-video__video--desktop');
    this.refs.unmuteBtn = document.getElementById(`unmute-btn-${this.sectionId}`);
    this.refs.muteBtn = document.getElementById(`mute-btn-${this.sectionId}`);
    this.refs.playBtn = document.getElementById(`play-btn-${this.sectionId}`);
    this.refs.pauseBtn = document.getElementById(`pause-btn-${this.sectionId}`);
  }

  #validateElements() {
    // Check if we have at least one video
    if (!this.refs.mobileVideo && !this.refs.desktopVideo) {
      this.handleError(new Error('No videos found'), 'No mobile or desktop video elements');
      return false;
    }

    // Check if we have all control buttons
    if (!this.refs.unmuteBtn || !this.refs.muteBtn || !this.refs.playBtn || !this.refs.pauseBtn) {
      this.handleError(new Error('Control buttons not found'), 'Missing control buttons');
      return false;
    }

    return true;
  }

  #initializeState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`lpScrollVideoData.${this.sectionId}`, {
      isPlaying: false,
      isMuted: true,
    });
  }

  #bindEvents() {
    // Mute/Unmute functionality
    if (this.refs.unmuteBtn) {
      const unmuteHandler = () => this.#handleUnmute();
      this.refs.unmuteBtn.addEventListener('click', unmuteHandler);
      this.#eventListeners.push({ element: this.refs.unmuteBtn, event: 'click', handler: unmuteHandler });
    }

    if (this.refs.muteBtn) {
      const muteHandler = () => this.#handleMute();
      this.refs.muteBtn.addEventListener('click', muteHandler);
      this.#eventListeners.push({ element: this.refs.muteBtn, event: 'click', handler: muteHandler });
    }

    // Play/Pause functionality
    if (this.refs.playBtn) {
      const playHandler = () => this.#handlePlay();
      this.refs.playBtn.addEventListener('click', playHandler);
      this.#eventListeners.push({ element: this.refs.playBtn, event: 'click', handler: playHandler });
    }

    if (this.refs.pauseBtn) {
      const pauseHandler = () => this.#handlePause();
      this.refs.pauseBtn.addEventListener('click', pauseHandler);
      this.#eventListeners.push({ element: this.refs.pauseBtn, event: 'click', handler: pauseHandler });
    }

    // Video event listeners for both videos
    [this.refs.mobileVideo, this.refs.desktopVideo].forEach(video => {
      if (!video) return;

      const playHandler = () => this.#handleVideoPlay();
      const pauseHandler = () => this.#handleVideoPause();
      const endedHandler = () => this.#handleVideoEnded();

      video.addEventListener('play', playHandler);
      video.addEventListener('pause', pauseHandler);
      video.addEventListener('ended', endedHandler);

      this.#eventListeners.push({ element: video, event: 'play', handler: playHandler });
      this.#eventListeners.push({ element: video, event: 'pause', handler: pauseHandler });
      this.#eventListeners.push({ element: video, event: 'ended', handler: endedHandler });
    });

    // Handle viewport resize
    const resizeHandler = () => this.#handleResize();
    window.addEventListener('resize', resizeHandler);
    this.#eventListeners.push({ element: window, event: 'resize', handler: resizeHandler });
  }

  #initializeButtonStates() {
    const activeVideo = this.#getVisibleVideo();
    if (!activeVideo) return;

    // Set initial button states based on video state
    if (activeVideo.paused) {
      this.#showPlayButton();
    } else {
      this.#showPauseButton();
    }

    if (activeVideo.muted) {
      this.#showUnmuteButton();
    } else {
      this.#showMuteButton();
    }
  }

  // Get the currently visible video based on CSS display
  #getVisibleVideo() {
    if (this.refs.mobileVideo && window.getComputedStyle(this.refs.mobileVideo).display !== 'none') {
      return this.refs.mobileVideo;
    }
    if (this.refs.desktopVideo && window.getComputedStyle(this.refs.desktopVideo).display !== 'none') {
      return this.refs.desktopVideo;
    }
    return null;
  }

  #handleUnmute() {
    try {
      const video = this.#getVisibleVideo();
      if (video) {
        video.muted = false;
        this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isMuted`, false);
        this.#showMuteButton();
      }
    } catch (error) {
      this.handleError(error, 'Failed to unmute video');
    }
  }

  #handleMute() {
    try {
      const video = this.#getVisibleVideo();
      if (video) {
        video.muted = true;
        this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isMuted`, true);
        this.#showUnmuteButton();
      }
    } catch (error) {
      this.handleError(error, 'Failed to mute video');
    }
  }

  #handlePlay() {
    try {
      const video = this.#getVisibleVideo();
      if (video) {
        video.play().catch(() => {});
        this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isPlaying`, true);
      }
    } catch (error) {
      this.handleError(error, 'Failed to play video');
    }
  }

  #handlePause() {
    try {
      const video = this.#getVisibleVideo();
      if (video) {
        video.pause();
        this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isPlaying`, false);
      }
    } catch (error) {
      this.handleError(error, 'Failed to pause video');
    }
  }

  #handleVideoPlay() {
    const video = this.#getVisibleVideo();
    if (!video) return;

    // Only update UI if this is the visible video
    const playingVideo = [this.refs.mobileVideo, this.refs.desktopVideo].find(v => v && !v.paused);
    if (playingVideo === video) {
      this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isPlaying`, true);
      this.#showPauseButton();
    }
  }

  #handleVideoPause() {
    const video = this.#getVisibleVideo();
    if (!video) return;

    // Only update UI if this is the visible video
    if (video.paused) {
      this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isPlaying`, false);
      this.#showPlayButton();
    }
  }

  #handleVideoEnded() {
    const video = this.#getVisibleVideo();
    if (!video) return;

    this.setGlobalData(`lpScrollVideoData.${this.sectionId}.isPlaying`, false);
    this.#showPlayButton();
  }

  #handleResize() {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      this.#initializeButtonStates();
    }, 250);
  }

  // Helper functions to show/hide buttons
  #showPlayButton() {
    this.refs.playBtn.style.display = 'flex';
    this.refs.pauseBtn.style.display = 'none';
  }

  #showPauseButton() {
    this.refs.playBtn.style.display = 'none';
    this.refs.pauseBtn.style.display = 'flex';
  }

  #showUnmuteButton() {
    this.refs.unmuteBtn.style.display = 'flex';
    this.refs.muteBtn.style.display = 'none';
  }

  #showMuteButton() {
    this.refs.unmuteBtn.style.display = 'none';
    this.refs.muteBtn.style.display = 'flex';
  }

  // Public API methods
  play() {
    this.#handlePlay();
  }

  pause() {
    this.#handlePause();
  }

  mute() {
    this.#handleMute();
  }

  unmute() {
    this.#handleUnmute();
  }

  isPlaying() {
    return this.getGlobalData(`lpScrollVideoData.${this.sectionId}.isPlaying`) || false;
  }

  isMuted() {
    return this.getGlobalData(`lpScrollVideoData.${this.sectionId}.isMuted`) || false;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up resize timeout
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }

    // Clean up global reference
    if (window.neemans.lpScrollVideoControls?.[this.sectionId]) {
      delete window.neemans.lpScrollVideoControls[this.sectionId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSLPScrollVideoControls() {
  if (!window.NMSBaseClass) {
    console.error('NMSLPScrollVideoControls: NMSBaseClass required');
    return;
  }

  // Find all scroll video sections with controls
  const elements = document.querySelectorAll('[data-nms-scroll-video]');

  for (const element of elements) {
    // Only initialize if controls are present
    const hasControls = element.querySelector('.nms-lp-scroll-video__controls');
    if (!hasControls) continue;

    if (!element.hasAttribute('data-controls-initialized')) {
      const instance = new NMSLPScrollVideoControls(element);
      instance.init();
      element.setAttribute('data-controls-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSLPScrollVideoControls);
} else {
  initNMSLPScrollVideoControls();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-scroll-video]')) {
    initNMSLPScrollVideoControls();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSLPScrollVideoControls = NMSLPScrollVideoControls;

// Global initialization function for other components to use
window.initNMSLPScrollVideoControls = initNMSLPScrollVideoControls;
